import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, ClipboardPaste, Save, Trash2, Loader2, RefreshCw, Inbox, Webhook, Copy, Package, Plus, Star, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL_SYMBOLS = ["COPPER", "ALUMINIUM", "GOLD", "SILVER", "ZINC", "LEAD", "NICKEL", "SOLAR", "LITHIUM"];
const TOKEN_KEY = "mm_admin_token";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
const panelCls = "border border-white/10 bg-[#0F0F0F] p-6 sm:p-8 space-y-5";
const inputCls = "rounded-none border-white/15 bg-[#121212] text-[#FDFDFD] font-ticker";

const EMPTY_PROD = { name: "", category: "ALUMINIUM", price: "", unit: "INR / Kg", image: "" };
const EMPTY_REVIEW = { name: "", company: "", rating: 5, text: "" };

const fileToDataUrl = (file) =>
  new Promise((res) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      const scale = Math.min(1, 800 / img.width);
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      res(c.toDataURL("image/jpeg", 0.8));
    };
    img.src = URL.createObjectURL(file);
  });

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [text, setText] = useState("");
  const [parsed, setParsed] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);

  const [manual, setManual] = useState(Object.fromEntries(ALL_SYMBOLS.map((s) => [s, ""])));
  const [overrides, setOverrides] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [prodForm, setProdForm] = useState(EMPTY_PROD);
  const [savingProd, setSavingProd] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [revForm, setRevForm] = useState(EMPTY_REVIEW);
  const [savingRev, setSavingRev] = useState(false);
  const [subs, setSubs] = useState([]);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const loadAll = useCallback(async () => {
    try {
      const [o, e, w, p, r, s] = await Promise.all([
        axios.get(`${API}/admin/overrides`, auth()),
        axios.get(`${API}/enquiries`, auth()),
        axios.get(`${API}/admin/webhook-info`, auth()),
        axios.get(`${API}/products`),
        axios.get(`${API}/reviews`),
        axios.get(`${API}/admin/subscribers`, auth()),
      ]);
      setOverrides(o.data.overrides);
      setEnquiries(e.data.enquiries);
      setWebhookInfo(w.data);
      setProducts(p.data.products);
      setReviews(r.data.reviews);
      setSubs(s.data.subscribers);
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  }, [logout]);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  const addReview = async (e) => {
    e.preventDefault();
    if (!revForm.name.trim() || revForm.text.trim().length < 5) return toast.error("Name and review text required");
    setSavingRev(true);
    try {
      await axios.post(`${API}/admin/reviews`, { ...revForm, rating: Number(revForm.rating) }, auth());
      toast.success("Review added — live on the website!");
      setRevForm(EMPTY_REVIEW);
      loadAll();
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Could not add review");
    } finally {
      setSavingRev(false);
    }
  };

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, auth());
      toast.success("Review deleted");
      loadAll();
    } catch {
      toast.error("Could not delete review");
    }
  };

  const deleteSub = async (id) => {
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, auth());
      toast.success("Subscriber removed");
      loadAll();
    } catch {
      toast.error("Could not remove subscriber");
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Logged in!");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const parse = async () => {
    if (!text.trim()) return toast.error("Paste the WhatsApp message first");
    setParsing(true);
    try {
      const res = await axios.post(`${API}/admin/parse`, { text }, auth());
      if (res.data.parsed.length === 0) toast.error("No rates found. Check the message.");
      setParsed(res.data.parsed);
    } catch (e) {
      if (e.response?.status === 401) logout();
      else toast.error("Could not parse");
    } finally {
      setParsing(false);
    }
  };

  const applyParsed = async () => {
    setApplying(true);
    try {
      await axios.post(`${API}/admin/rates`, { rates: parsed.map((p) => ({ symbol: p.symbol, mcx: p.mcx })) }, auth());
      toast.success(`${parsed.length} rates updated — live on the website!`);
      setParsed([]);
      setText("");
      loadAll();
    } catch (e) {
      toast.error("Could not apply rates");
    } finally {
      setApplying(false);
    }
  };

  const saveManual = async (symbol) => {
    const val = parseFloat(manual[symbol]);
    if (!val || val <= 0) return toast.error("Enter a valid rate");
    try {
      await axios.post(`${API}/admin/rates`, { rates: [{ symbol, mcx: val }] }, auth());
      toast.success(`${symbol} set to ₹${val}`);
      setManual({ ...manual, [symbol]: "" });
      loadAll();
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const removeOverride = async (symbol) => {
    try {
      await axios.delete(`${API}/admin/rates/${symbol}`, auth());
      toast.success(`${symbol} override removed — live feed restored`);
      loadAll();
    } catch (e) {
      toast.error("Could not remove");
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    const price = parseFloat(prodForm.price);
    if (!prodForm.name.trim() || !price || price <= 0) return toast.error("Name and valid price required");
    setSavingProd(true);
    try {
      await axios.post(`${API}/admin/products`, { ...prodForm, price }, auth());
      toast.success(`${prodForm.name} added — live on website!`);
      setProdForm(EMPTY_PROD);
      loadAll();
    } catch (err) {
      toast.error("Could not add product");
    } finally {
      setSavingProd(false);
    }
  };

  const updateProductPrice = async (pid, price) => {
    const val = parseFloat(price);
    if (!val || val <= 0) return toast.error("Enter a valid price");
    try {
      await axios.put(`${API}/admin/products/${pid}`, { price: val }, auth());
      toast.success("Price updated");
      loadAll();
    } catch (err) {
      toast.error("Could not update");
    }
  };

  const deleteProduct = async (pid) => {
    try {
      await axios.delete(`${API}/admin/products/${pid}`, auth());
      toast.success("Product removed");
      loadAll();
    } catch (err) {
      toast.error("Could not remove");
    }
  };

  const copyText = (t) => {
    navigator.clipboard.writeText(t);
    toast.success("Copied!");
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-[#0F0F0F] p-8 sm:p-10 space-y-6">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center bg-[#25D366] text-[#0A0A0A]">
              <Lock size={17} />
            </span>
            <div>
              <p className="font-display font-extrabold uppercase tracking-tight text-lg">Admin Panel</p>
              <p className="font-ticker text-[10px] tracking-[0.25em] uppercase text-white/40">Mahima Metals</p>
            </div>
          </div>
          <div className="space-y-2">
            <label className="font-ticker text-[10px] tracking-[0.25em] uppercase text-white/55">Password</label>
            <Input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              className={`${inputCls} h-12`}
            />
          </div>
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-btn"
            className="w-full inline-flex items-center justify-center gap-3 bg-[#25D366] px-7 py-4 font-ticker text-[11px] tracking-[0.25em] uppercase font-semibold text-[#0A0A0A] hover:bg-[#7BE3A6] transition-colors duration-300 disabled:opacity-60"
          >
            {loggingIn ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />} Login
          </button>
        </form>
      </div>
    );
  }

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/webhook`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] px-6 sm:px-10 lg:px-16 py-10" data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
        <div>
          <p className="font-ticker text-[11px] tracking-[0.4em] uppercase text-[#25D366] mb-1">Admin Panel</p>
          <h1 className="font-display font-black uppercase tracking-tighter text-3xl sm:text-4xl">
            Rate <span className="text-[#25D366]">Manager</span>
          </h1>
        </div>
        <div className="flex gap-3">
          <a href="/" className="inline-flex items-center gap-2 border border-white/20 px-5 py-2.5 font-ticker text-[10px] tracking-[0.2em] uppercase text-white/70 hover:border-[#25D366] hover:text-[#25D366] transition-colors" data-testid="admin-view-site-btn">
            View Website
          </a>
          <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 bg-white/10 text-white px-5 py-2.5 font-ticker text-[10px] tracking-[0.2em] uppercase hover:bg-[#FF3B30] transition-colors">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className={panelCls} data-testid="admin-whatsapp-section">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center bg-[#25D366]/15 text-[#25D366]">
                <ClipboardPaste size={16} />
              </span>
              <div>
                <p className="font-display font-extrabold uppercase tracking-tight">WhatsApp Rate Paste</p>
                <p className="text-xs text-white/50">Paste the rate message from WhatsApp and hit Parse</p>
              </div>
            </div>
            <Textarea
              data-testid="admin-whatsapp-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"e.g.\nCopper 892.45\nAluminium 246.80\nGold 101850\nSolar 310\nLithium 2450"}
              className={`${inputCls} min-h-[150px] text-sm placeholder:text-white/25`}
            />
            <button onClick={parse} disabled={parsing} data-testid="admin-parse-btn" className="inline-flex items-center gap-2 bg-white/10 text-white px-6 py-3 font-ticker text-[10px] tracking-[0.2em] uppercase hover:bg-[#25D366] hover:text-[#0A0A0A] transition-colors disabled:opacity-60">
              {parsing ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Parse
            </button>

            {parsed.length > 0 && (
              <div className="space-y-3 pt-2" data-testid="admin-parsed-preview">
                <p className="font-ticker text-[10px] tracking-[0.25em] uppercase text-white/50">
                  Found {parsed.length} rates — review and apply:
                </p>
                {parsed.map((p, i) => (
                  <div key={p.symbol} className="flex items-center gap-3 border border-white/10 bg-[#121212] px-4 py-3">
                    <span className="font-ticker text-xs font-semibold tracking-[0.15em] w-28">{p.symbol}</span>
                    <span className="text-white/40">₹</span>
                    <Input
                      data-testid={`admin-parsed-input-${p.symbol.toLowerCase()}`}
                      type="number"
                      step="any"
                      value={p.mcx}
                      onChange={(e) => {
                        const next = [...parsed];
                        next[i] = { ...p, mcx: parseFloat(e.target.value) || 0 };
                        setParsed(next);
                      }}
                      className={`${inputCls} h-9 max-w-[140px]`}
                    />
                    <span className="text-[10px] text-white/30 truncate hidden sm:block">{p.line}</span>
                  </div>
                ))}
                <button onClick={applyParsed} disabled={applying} data-testid="admin-apply-parsed-btn" className="inline-flex items-center gap-2 bg-[#25D366] text-[#0A0A0A] px-6 py-3 font-ticker text-[10px] tracking-[0.2em] uppercase font-semibold hover:bg-[#7BE3A6] transition-colors disabled:opacity-60">
                  {applying ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Apply All
                </button>
              </div>
            )}
          </div>

          <div className={panelCls} data-testid="admin-webhook-section">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center bg-[#25D366]/15 text-[#25D366]">
                <Webhook size={16} />
              </span>
              <div>
                <p className="font-display font-extrabold uppercase tracking-tight">Full-Auto WhatsApp (Meta Cloud API)</p>
                <p className="text-xs text-white/50">
                  Status:{" "}
                  {webhookInfo?.signature_check ? (
                    <span className="text-[#00C851]">Configured & secured</span>
                  ) : (
                    <span className="text-[#25D366]">Endpoint ready — Meta setup pending</span>
                  )}
                </p>
              </div>
            </div>
            <div className="space-y-3 font-ticker text-xs">
              <div className="flex items-center justify-between gap-3 border border-white/10 bg-[#121212] px-4 py-3">
                <div className="min-w-0">
                  <p className="text-[9px] tracking-[0.25em] uppercase text-white/40 mb-1">Callback URL</p>
                  <p className="truncate text-white/80" data-testid="admin-webhook-url">{webhookUrl}</p>
                </div>
                <button onClick={() => copyText(webhookUrl)} data-testid="admin-copy-webhook-url" className="text-white/50 hover:text-[#25D366] transition-colors shrink-0" aria-label="Copy webhook URL">
                  <Copy size={14} />
                </button>
              </div>
              <div className="flex items-center justify-between gap-3 border border-white/10 bg-[#121212] px-4 py-3">
                <div className="min-w-0">
                  <p className="text-[9px] tracking-[0.25em] uppercase text-white/40 mb-1">Verify Token</p>
                  <p className="truncate text-white/80" data-testid="admin-webhook-token">{webhookInfo?.verify_token || "—"}</p>
                </div>
                <button onClick={() => copyText(webhookInfo?.verify_token || "")} data-testid="admin-copy-verify-token" className="text-white/50 hover:text-[#25D366] transition-colors shrink-0" aria-label="Copy verify token">
                  <Copy size={14} />
                </button>
              </div>
              <p className="text-white/40 leading-relaxed normal-case">
                Setup: developers.facebook.com → Create App (WhatsApp) → Configuration → paste the Callback URL and Verify Token above → subscribe to "messages". Once your rate provider sends messages to that WhatsApp Business number, rates will update automatically.
              </p>
            </div>
          </div>

          <div className={panelCls} data-testid="admin-enquiries-section">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center bg-[#25D366]/15 text-[#25D366]">
                <Inbox size={16} />
              </span>
              <div>
                <p className="font-display font-extrabold uppercase tracking-tight">Enquiry Inbox ({enquiries.length})</p>
                <p className="text-xs text-white/50">Customer enquiries from the website contact form</p>
              </div>
            </div>
            {enquiries.length === 0 ? (
              <p className="font-ticker text-xs text-white/40" data-testid="admin-no-enquiries">No enquiries yet</p>
            ) : (
              <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="admin-enquiries-list">
                {enquiries.map((q, i) => (
                  <div key={i} className="border border-white/10 bg-[#121212] px-4 py-3" data-testid={`admin-enquiry-${i}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="font-ticker text-sm font-semibold">{q.name}</p>
                      <a href={`tel:${q.phone}`} className="font-ticker text-xs text-[#25D366] hover:text-[#7BE3A6]">{q.phone}</a>
                    </div>
                    <p className="font-ticker text-[10px] tracking-[0.15em] uppercase text-white/40 mt-1">
                      {q.metal} · {q.created_at ? new Date(q.created_at).toLocaleString("en-IN") : ""}
                    </p>
                    {q.message && <p className="text-xs text-white/60 mt-2">{q.message}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={panelCls} data-testid="admin-subscribers-section">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center bg-[#25D366]/15 text-[#25D366]">
                <Users size={16} />
              </span>
              <div>
                <p className="font-display font-extrabold uppercase tracking-tight">Rate Alert Subscribers ({subs.length})</p>
                <p className="text-xs text-white/50">Visitors who want daily metal rates on WhatsApp — use "Share Today's Rates" to send them</p>
              </div>
            </div>
            {subs.length === 0 ? (
              <p className="font-ticker text-xs text-white/40" data-testid="admin-no-subscribers">No subscribers yet</p>
            ) : (
              <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1" data-testid="admin-subscribers-list">
                {subs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 border border-white/10 bg-[#121212] px-4 py-3" data-testid={`admin-subscriber-${s.id}`}>
                    <div className="min-w-0">
                      <a href={`https://wa.me/91${s.phone}`} target="_blank" rel="noopener noreferrer" className="font-ticker text-sm font-semibold text-[#25D366] hover:text-[#7BE3A6]">
                        +91 {s.phone}
                      </a>
                      <p className="font-ticker text-[10px] tracking-[0.15em] uppercase text-white/40 mt-0.5 truncate">
                        {s.name || "—"} · {s.created_at ? new Date(s.created_at).toLocaleDateString("en-IN") : ""}
                      </p>
                    </div>
                    <button onClick={() => deleteSub(s.id)} data-testid={`admin-subscriber-delete-${s.id}`} className="text-[#FF3B30] hover:text-[#FF6B60] transition-colors shrink-0" aria-label="Remove subscriber">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className={panelCls} data-testid="admin-products-section">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center bg-[#25D366]/15 text-[#25D366]">
                <Package size={16} />
              </span>
              <div>
                <p className="font-display font-extrabold uppercase tracking-tight">Products ({products.length})</p>
                <p className="text-xs text-white/50">Add your product photo + price — shows instantly on the website</p>
              </div>
            </div>

            <form onSubmit={addProduct} className="space-y-3 border border-white/10 bg-[#121212] p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  data-testid="admin-product-name-input"
                  value={prodForm.name}
                  onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })}
                  placeholder="Product name (e.g. Copper Wire)"
                  className={`${inputCls} h-10`}
                />
                <select
                  data-testid="admin-product-category-select"
                  value={prodForm.category}
                  onChange={(e) => setProdForm({ ...prodForm, category: e.target.value })}
                  className="h-10 rounded-none border border-white/15 bg-[#121212] px-3 text-sm text-[#FDFDFD] focus:outline-none focus:ring-1 focus:ring-[#25D366]"
                >
                  <option value="ALUMINIUM">ALUMINIUM</option>
                  <option value="COPPER">COPPER</option>
                  <option value="SOLAR">SOLAR</option>
                  <option value="LITHIUM">LITHIUM</option>
                  <option value="E-WASTE">E-WASTE</option>
                  <option value="BRASS">BRASS</option>
                  <option value="SS">SS</option>
                  <option value="MS">MS</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  data-testid="admin-product-price-input"
                  type="number"
                  step="any"
                  value={prodForm.price}
                  onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })}
                  placeholder="Price ₹"
                  className={`${inputCls} h-10`}
                />
                <Input
                  data-testid="admin-product-unit-input"
                  value={prodForm.unit}
                  onChange={(e) => setProdForm({ ...prodForm, unit: e.target.value })}
                  placeholder="Unit (INR / Kg)"
                  className={`${inputCls} h-10`}
                />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-ticker text-[10px] tracking-[0.15em] uppercase text-white/70 hover:border-[#25D366] hover:text-[#25D366] transition-colors cursor-pointer">
                  <Plus size={12} /> Photo
                  <input
                    data-testid="admin-product-photo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        const data = await fileToDataUrl(f);
                        setProdForm((prev) => ({ ...prev, image: data }));
                        toast.success("Photo attached");
                      }
                    }}
                  />
                </label>
                {prodForm.image && <img src={prodForm.image} alt="preview" className="h-10 w-10 object-cover border border-white/15" />}
                <button
                  type="submit"
                  disabled={savingProd}
                  data-testid="admin-product-add-btn"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-[#0A0A0A] px-5 py-2.5 font-ticker text-[10px] tracking-[0.2em] uppercase font-semibold hover:bg-[#7BE3A6] transition-colors disabled:opacity-60"
                >
                  {savingProd ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Add Product
                </button>
              </div>
            </form>

            <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1" data-testid="admin-products-list">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-3 border border-white/10 bg-[#121212] px-3 py-2.5" data-testid={`admin-product-${p.id}`}>
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="h-11 w-11 object-cover border border-white/10 shrink-0" />
                  ) : (
                    <span className="flex h-11 w-11 items-center justify-center border border-white/10 text-white/20 font-display font-black text-xs shrink-0">{p.name.slice(0, 2).toUpperCase()}</span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-ticker text-xs font-semibold truncate">{p.name}</p>
                    <p className="font-ticker text-[9px] tracking-[0.2em] uppercase text-white/35">{p.category} · {p.unit}</p>
                  </div>
                  <Input
                    type="number"
                    step="any"
                    defaultValue={p.price}
                    data-testid={`admin-product-price-${p.id}`}
                    onBlur={(e) => parseFloat(e.target.value) !== p.price && updateProductPrice(p.id, e.target.value)}
                    className={`${inputCls} h-9 w-24 shrink-0`}
                  />
                  <button onClick={() => deleteProduct(p.id)} data-testid={`admin-product-delete-${p.id}`} className="text-[#FF3B30] hover:text-[#FF6B60] transition-colors shrink-0" aria-label={`Delete ${p.name}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className={panelCls} data-testid="admin-manual-section">
            <div>
              <p className="font-display font-extrabold uppercase tracking-tight">Manual Rate Entry</p>
              <p className="text-xs text-white/50">Set any metal, Solar or Lithium rate by hand (₹)</p>
            </div>
            <div className="space-y-2.5">
              {ALL_SYMBOLS.map((s) => (
                <div key={s} className="flex items-center gap-3">
                  <span className={`font-ticker text-xs font-semibold tracking-[0.15em] w-28 ${s === "SOLAR" || s === "LITHIUM" ? "text-[#25D366]" : "text-white"}`}>
                    {s}
                  </span>
                  <span className="text-white/40">₹</span>
                  <Input
                    data-testid={`admin-manual-input-${s.toLowerCase()}`}
                    type="number"
                    step="any"
                    value={manual[s]}
                    onChange={(e) => setManual({ ...manual, [s]: e.target.value })}
                    placeholder="Rate"
                    className={`${inputCls} h-10 max-w-[160px]`}
                  />
                  <button onClick={() => saveManual(s)} data-testid={`admin-manual-save-${s.toLowerCase()}`} className="inline-flex items-center gap-1.5 border border-white/15 px-4 py-2 font-ticker text-[10px] tracking-[0.15em] uppercase text-white/70 hover:border-[#25D366] hover:text-[#25D366] transition-colors">
                    <Save size={12} /> Set
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className={panelCls} data-testid="admin-overrides-section">
            <div>
              <p className="font-display font-extrabold uppercase tracking-tight">Active Overrides</p>
              <p className="text-xs text-white/50">These rates are showing on the website instead of the live feed</p>
            </div>
            {overrides.length === 0 ? (
              <p className="font-ticker text-xs text-white/40" data-testid="admin-no-overrides">No overrides active — everything is running on the live feed</p>
            ) : (
              overrides.map((o) => (
                <div key={o.symbol} className="flex items-center justify-between border border-white/10 bg-[#121212] px-4 py-3" data-testid={`admin-override-${o.symbol.toLowerCase()}`}>
                  <div>
                    <span className="font-ticker text-xs font-semibold tracking-[0.15em]">{o.symbol}</span>
                    <span className="ml-3 font-ticker text-sm text-[#25D366] font-semibold">₹{o.mcx}</span>
                    <span className="ml-3 text-[10px] text-white/35">
                      {o.source === "whatsapp_auto" ? "auto · " : ""}
                      {new Date(o.updated_at).toLocaleString("en-IN")}
                    </span>
                  </div>
                  <button onClick={() => removeOverride(o.symbol)} data-testid={`admin-remove-override-${o.symbol.toLowerCase()}`} className="text-[#FF3B30] hover:text-[#FF6B60] transition-colors" aria-label={`Remove ${o.symbol} override`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className={panelCls} data-testid="admin-reviews-section">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center bg-[#25D366]/15 text-[#25D366]">
                <Star size={16} />
              </span>
              <div>
                <p className="font-display font-extrabold uppercase tracking-tight">Customer Reviews ({reviews.length})</p>
                <p className="text-xs text-white/50">These reviews show on the website homepage</p>
              </div>
            </div>

            <form onSubmit={addReview} className="space-y-3 border border-white/10 bg-[#121212] p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  data-testid="admin-review-name-input"
                  value={revForm.name}
                  onChange={(e) => setRevForm({ ...revForm, name: e.target.value })}
                  placeholder="Customer name"
                  className={`${inputCls} h-10`}
                />
                <Input
                  data-testid="admin-review-company-input"
                  value={revForm.company}
                  onChange={(e) => setRevForm({ ...revForm, company: e.target.value })}
                  placeholder="Company / City (optional)"
                  className={`${inputCls} h-10`}
                />
              </div>
              <Textarea
                data-testid="admin-review-text-input"
                value={revForm.text}
                onChange={(e) => setRevForm({ ...revForm, text: e.target.value })}
                placeholder="Review text…"
                rows={2}
                className={`${inputCls} rounded-none`}
              />
              <div className="flex items-center gap-3">
                <select
                  data-testid="admin-review-rating-select"
                  value={revForm.rating}
                  onChange={(e) => setRevForm({ ...revForm, rating: e.target.value })}
                  className="h-10 rounded-none border border-white/15 bg-[#121212] px-3 text-sm text-[#FDFDFD] focus:outline-none focus:ring-1 focus:ring-[#25D366]"
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>{n} ★</option>
                  ))}
                </select>
                <button
                  type="submit"
                  disabled={savingRev}
                  data-testid="admin-review-add-btn"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-[#0A0A0A] px-5 py-2.5 font-ticker text-[10px] tracking-[0.2em] uppercase font-semibold hover:bg-[#7BE3A6] transition-colors disabled:opacity-60"
                >
                  {savingRev ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Add Review
                </button>
              </div>
            </form>

            <div className="space-y-2.5 max-h-[360px] overflow-y-auto pr-1" data-testid="admin-reviews-list">
              {reviews.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-3 border border-white/10 bg-[#121212] px-4 py-3" data-testid={`admin-review-${r.id}`}>
                  <div className="min-w-0">
                    <p className="font-ticker text-xs font-semibold">
                      {r.name} <span className="text-[#25D366] ml-1">{"★".repeat(r.rating)}</span>
                    </p>
                    {r.company && <p className="font-ticker text-[9px] tracking-[0.2em] uppercase text-white/35 mt-0.5">{r.company}</p>}
                    <p className="text-xs text-white/55 mt-1.5 line-clamp-2">{r.text}</p>
                  </div>
                  <button onClick={() => deleteReview(r.id)} data-testid={`admin-review-delete-${r.id}`} className="text-[#FF3B30] hover:text-[#FF6B60] transition-colors shrink-0" aria-label={`Delete review by ${r.name}`}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
